import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Mail, Phone, Globe, ArrowUpRight } from "lucide-react";
import { LogoLockup } from "./Logo";
import { SERVICES } from "@/data/services";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/contact" },
];

export default function Footer() {
  return (
    <footer
      data-testid="footer"
      className="relative bg-primary text-primary-foreground pt-20 pb-10 overflow-hidden"
    >
      <div className="absolute inset-0 -z-0 opacity-[0.05]">
        <div className="absolute top-10 right-10 w-80 h-80 rounded-full border border-white" />
        <div className="absolute -bottom-32 -left-10 w-96 h-96 rounded-full border border-white" />
      </div>

      <div className="max-page container-px relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 pb-16 border-b border-white/10">
          <div className="lg:col-span-5">
            <LogoLockup height={44} variant="dark" />
            <p className="mt-6 max-w-sm text-primary-foreground/70 leading-relaxed text-sm">
              A unified general trading partner — sourcing, supply and service
              across nine specialised verticals. Built for the operators who
              expect more.
            </p>

            <div className="mt-8 space-y-3 text-sm text-primary-foreground/80">
              <a
                href="tel:+971555752118"
                className="flex items-center gap-3 hover:text-primary-foreground transition-colors"
              >
                <Phone className="w-4 h-4" strokeWidth={1.6} /> 055 575 2118
              </a>
              <a
                href="mailto:muzakir@one-sphere.com"
                className="flex items-center gap-3 hover:text-primary-foreground transition-colors"
              >
                <Mail className="w-4 h-4" strokeWidth={1.6} /> muzakir@one-sphere.com
              </a>
              <a
                href="https://www.one-sphere.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 hover:text-primary-foreground transition-colors"
              >
                <Globe className="w-4 h-4" strokeWidth={1.6} /> www.one-sphere.com
              </a>
            </div>

            <div className="mt-8 flex items-center gap-3">
              {[
                { Icon: Linkedin, href: "#", l: "LinkedIn" },
                { Icon: Instagram, href: "#", l: "Instagram" },
                { Icon: Mail, href: "mailto:muzakir@one-sphere.com", l: "Email" },
              ].map(({ Icon, href, l }) => (
                <a
                  key={l}
                  href={href}
                  aria-label={l}
                  data-testid={`footer-social-${l.toLowerCase()}`}
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 hover:border-white/30 transition-all duration-500"
                >
                  <Icon className="w-4 h-4" strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-primary-foreground/50 mb-5">
              Navigate
            </div>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-nav-${n.label.toLowerCase()}`}
                    className="group text-sm text-primary-foreground/80 hover:text-primary-foreground inline-flex items-center gap-2"
                  >
                    {n.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.25em] text-primary-foreground/50 mb-5">
              Verticals
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 hidden lg:block">
          <div className="font-sans font-extrabold tracking-tighter text-[18vw] leading-[0.85] text-primary-foreground/[0.06] select-none uppercase">
            ONE&nbsp;SPHERE
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 text-xs text-primary-foreground/50">
          <div>© {new Date().getFullYear()} One Sphere Trading. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-primary-foreground">Privacy</a>
            <a href="#" className="hover:text-primary-foreground">Terms</a>
            <a href="#" className="hover:text-primary-foreground">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
